import { Vob } from '@finpay/estimation-types';

/**
 * Converts a CRM VOB record to the new VOB format
 * @param ptRecord The patient record from CRM
 * @param patientId The patient ID
 * @returns A new VOB object
 */
export const convertCRMVOBToNewVob = (ptRecord: any, patientId: number): Vob => {
    const vob: Vob = {
        patientId: patientId,
        client: {
            clientId: ptRecord?.clientId ?? undefined,
            npi: "default value",// TODO:
        },
        facility: {
            facilityId: ptRecord?.facilityId ?? undefined,
            npi: "default value", // TODO:
        },
        payor: {
            payorId: ptRecord?.payer?.payorId ?? undefined,
            payorPlanId: ptRecord?.plan?.payorPlanId ?? undefined,
        },
        plan: {
            payorPlanId: ptRecord?.plan?.payorPlanId ?? undefined,
        },
        vobSourceId: 3, // CRM
        isValid: false,
        digitalVerificationMethod: ptRecord?.vob?.digitalVerificationMethod !== 'manual',//TODO
        groupNum: ptRecord?.vob?.groupNum ?? undefined,
        policyNum: ptRecord?.vob?.policyNum ?? undefined,
        liveVOB: ptRecord?.vob?.liveVOB ?? false, //TODO
        activePolicy: ptRecord?.vob?.activePolicy ?? false,
        isCarryover: ptRecord?.vob?.fourthQuarterCarryover ?? false,
        isCopayReset: ptRecord?.vob?.copayReset ?? false,
        policyBeginDate: ptRecord?.vob?.policyBeginDate ?? undefined,//TODO
        policyEndDate: ptRecord?.vob?.policyEndDate ?? undefined, //TODO
        innDeductible: {
            total: ptRecord?.vob?.inNetwDeductible ?? undefined,
            met: (ptRecord?.vob?.inNetwDeductible ?? 0) - (ptRecord?.vob?.inNetwDeductibleRemaining ?? 0),
            remaining: ptRecord?.vob?.inNetwDeductibleRemaining ?? 0,
        },
        innOopm: {
            total: ptRecord?.vob?.inNetwOopMax ?? undefined,
            met: (ptRecord?.vob?.inNetwOopMax ?? 0) - (ptRecord?.vob?.inNetwOopMaxRemaining ?? 0),
            remaining: ptRecord?.vob?.inNetwOopMaxRemaining ?? 0,
            isDeductibleIncluded: ptRecord?.vob?.inNetwOopIncluded ?? false,
        },
        innFamilyDeductible: {
            total: ptRecord?.vob?.inNetwFamilyDeductible ?? 0,
            met: (ptRecord?.vob?.inNetwFamilyDeductible ?? 0) - (ptRecord?.vob?.inNetwFamilyDeductibleRemaining ?? 0),
            remaining: ptRecord?.vob?.inNetwFamilyDeductibleRemaining ?? 0,
        },
        innFamilyOopm: {
            total: ptRecord?.vob?.inNetwFamilyOopMax ?? 0,
            met: (ptRecord?.vob?.inNetwFamilyOopMax ?? 0) - (ptRecord?.vob?.inNetwFamilyOopMaxRemaining ?? 0),
            remaining: ptRecord?.vob?.inNetwFamilyOopMaxRemaining ?? 0,
            isDeductibleIncluded: ptRecord?.vob?.inNetwOopIncluded ?? false,
        },
        innClassifications: ptRecord?.vob?.inNetwVobClassifications ? ptRecord.vob.inNetwVobClassifications.map((classification: any) => ({
            vobClassificationId: classification?.selectedClassification?.vobClassificationId ?? 0,
            copayType: classification?.coPaySelection ?? '',
            coInsurance: classification?.coInsurance ?? 0,
            coPay: classification?.coPay ?? 0,
            maxDays: classification?.maxDays ?? 0,
        })) : [],

        oonDeductible: {
            total: ptRecord?.vob?.ooNetwDeductible ?? 0,
            met: (ptRecord?.vob?.ooNetwDeductible ?? 0) - (ptRecord?.vob?.ooNetwDeductibleRemaining ?? 0),
            remaining: ptRecord?.vob?.ooNetwDeductibleRemaining ?? 0,
        },
        oonOopm: {
            total: ptRecord?.vob?.ooNetwOopMax ?? 0,
            met: (ptRecord?.vob?.ooNetwOopMax ?? 0) - (ptRecord?.vob?.ooNetwOopMaxRemaining ?? 0),
            remaining: ptRecord?.vob?.ooNetwOopMaxRemaining ?? 0,
            isDeductibleIncluded: ptRecord?.vob?.ooNetwOopIncluded ?? false,
        },
        oonFamilyDeductible: {
            total: ptRecord?.vob?.ooNetwFamilyDeductible ?? 0,
            met: (ptRecord?.vob?.ooNetwFamilyDeductible ?? 0) - (ptRecord?.vob?.ooNetwFamilyDeductibleRemaining ?? 0),
            remaining: ptRecord?.vob?.ooNetwFamilyDeductibleRemaining ?? 0,
        },
        oonFamilyOopm: {
            total: ptRecord?.vob?.ooNetwFamilyOopMax ?? 0,
            met: (ptRecord?.vob?.ooNetwFamilyOopMax ?? 0) - (ptRecord?.vob?.ooNetwFamilyOopMaxRemaining ?? 0),
            remaining: ptRecord?.vob?.ooNetwFamilyOopMaxRemaining ?? 0,
            isDeductibleIncluded: ptRecord?.vob?.ooNetwOopIncluded ?? false,
        },
        oonClassifications: ptRecord?.vob?.ooNetwVobClassifications ? ptRecord.vob.ooNetwVobClassifications.map((classification: any) => ({
            vobClassificationId: classification?.selectedClassification?.vobClassificationId ?? 0,
            copayType: classification?.coPaySelection ?? '',
            coInsurance: classification?.coInsurance ?? 0,
            coPay: classification?.coPay ?? 0,
            maxDays: classification?.maxDays ?? 0,
        })) : [],
        notes: ptRecord?.vob?.notes ? ptRecord.vob.notes.map((note: any) => ({
            noteText: note?.noteText ?? '',
            noteDt: note?.noteDt ?? new Date().toISOString(),
        })) : [],
        employer: ptRecord?.vob?.employer ? {
            employerId: ptRecord?.vob?.employer?.employerId ?? undefined,
            employerName: ptRecord?.vob?.employer?.employerName ?? '',
        } : undefined,
        sourceJson: ptRecord?.vob?.sourceJson,
    };

    // Only add groupNum if it's not empty after trimming
    if (ptRecord?.vob?.groupNum && ptRecord.vob.groupNum.trim() !== '') {
        vob.groupNum = ptRecord.vob.groupNum;
    }

    // Only add policyNum if it's not empty after trimming
    if (ptRecord?.vob?.policyNum && ptRecord.vob.policyNum.trim() !== '') {
        vob.policyNum = ptRecord.vob.policyNum;
    }

    return vob;
};
